import { Box, Flex, Text } from '@chakra-ui/react';
import VButton from '@voly/ui-components/src/v2/VButton.jsx';
import VDialogPW from '@voly/ui-components/src/v2/VDialogPW';
import VDraggable from '@voly/ui-components/src/v2/VDraggable/VDraggable';
import VEmptyState from '@voly/ui-components/src/v2/VEmptyState.jsx';
import { ReactElement, useState } from 'react';
import { translate } from 'src/app/project/features/translate/translate';
import {
  TAllCustomFields,
  TCustomField,
} from 'src/app/project/modules/custom-fields/custom-fields.model';
import CustomFieldGroup from '../molecules/CustomFieldGroup';
import NewCustomFieldGroupModal from '../molecules/NewCustomFieldGroupModal';
import RenameCustomFieldGroupModal from '../molecules/RenameCustomFieldGroupModal';
import SettingsCustomFieldRow from '../molecules/SettingsCustomFieldRow';
import VMaterialIcon from '@voly/ui-components/src/v2/icons/VMaterialIcon.jsx';

const AddIcon = (): ReactElement => <VMaterialIcon name="add" pack="" />;

interface CustomFieldGroupsSettingsProps {
  customFields: TAllCustomFields;
  addGroup: (name: string) => void;
  editCustomField: (fieldId: string) => void;
  addNewCustomField: (group?: TGroupOption) => void;
  workspaceId: string;
  fieldsLayout: { fieldsLayout: TLayoutConfig[] };
  handleReorder: (order: TLayoutEntry[]) => void;
  handleUngroup: (group: TLayoutGroup, order: TLayoutEntry[]) => void;
  renameGroup: (group: TLayoutGroup, name: string) => void;
  deleteCustomField: (fieldId: string) => void;
  deleteGroup: (group: TLayoutGroup) => void;
  setGroupVisibility: (group: TLayoutGroup, display: boolean) => void;
  setFieldVisibility: (field: TLayoutField, display: boolean) => void;
  setGroupLocked: (group: TLayoutGroup, locked: boolean) => void;
  setFieldLocked: (field: TLayoutField, locked: boolean) => void;
}

export type TLayoutItem = TCustomField;

export type TLayoutField = TCustomField;

export type TLayoutGroup = {
  id?: string;
  type: 'GROUP';
  title: string;
  description?: string | null;
  items: TLayoutItem[];
};

export type TLayoutEntry = TLayoutField | TLayoutGroup;

type TFieldConfig = { id: string; type: 'FIELD' };

export type TGroupConfig = {
  id?: string;
  type: 'GROUP';
  title: string;
  description?: string | null;
  items: { id: string }[];
};

export type TLayoutConfig = TFieldConfig | TGroupConfig;

export type TGroupOption = { label: string; value?: string };

type TDeleteDialog = {
  title: string;
  message: string;
  confirmText: string;
  confirm: () => void;
};

export const isGroup = (node: TLayoutEntry | TLayoutItem): node is TLayoutGroup => 'items' in node;

const toGroupOption = (group: TLayoutGroup): TGroupOption => ({
  label: group.title,
  value: group.id,
});

const CustomFieldGroupsSettings = (props: CustomFieldGroupsSettingsProps) => {
  const {
    addGroup,
    addNewCustomField,
    customFields,
    workspaceId,
    fieldsLayout,
    handleReorder,
    handleUngroup,
    renameGroup,
    deleteCustomField,
    deleteGroup,
    setGroupVisibility,
    setFieldVisibility,
    setGroupLocked,
    setFieldLocked,
    editCustomField,
  } = props;
  const resolveField = (id: string): TCustomField | undefined => customFields[workspaceId]?.[id];

  const fields: TLayoutEntry[] = fieldsLayout.fieldsLayout
    .map((item): TLayoutEntry | undefined => {
      if (item.type === 'FIELD') {
        return resolveField(item.id);
      }
      return {
        id: item.id,
        type: 'GROUP',
        title: item.title,
        description: item.description,
        items: item.items
          .map((groupItem) => resolveField(groupItem.id))
          .filter((field): field is TCustomField => field !== undefined),
      };
    })
    .filter((entry): entry is TLayoutEntry => entry !== undefined);

  const [layout, setLayout] = useState(fields);
  const [layoutSource, setLayoutSource] = useState(fieldsLayout.fieldsLayout);
  const [customFieldsSource, setCustomFieldsSource] = useState(customFields);
  if (layoutSource !== fieldsLayout.fieldsLayout || customFieldsSource !== customFields) {
    setLayoutSource(fieldsLayout.fieldsLayout);
    setCustomFieldsSource(customFields);
    setLayout(fields);
  }
  const [isNewGroupModalOpen, setIsNewGroupModalOpen] = useState(false);
  const [groupToRename, setGroupToRename] = useState<TLayoutGroup | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<TDeleteDialog | null>(null);

  const handleCreateGroup = () => {
    setIsNewGroupModalOpen(true);
  };

  const handleGroupCreated = (name: string) => {
    addGroup(name);
    setIsNewGroupModalOpen(false);
  };

  const handleGroupRenamed = (name: string): void => {
    if (!groupToRename) {
      return;
    }

    renameGroup(groupToRename, name);
    setGroupToRename(null);
  };

  const groupedFieldIds = new Set(
    layout.filter(isGroup).flatMap((group) => group.items.map((item) => item.id)),
  );

  const handleAddCustomField = (group?: TGroupOption) => {
    addNewCustomField(group);
  };

  const askToDeleteGroup = (group: TLayoutGroup): void =>
    setDeleteDialog({
      title: translate('delete_group', { groupName: group.title }),
      message: translate('delete_group_message'),
      confirmText: translate('delete_group_confirm'),
      confirm: () => deleteGroup(group),
    });

  const askToDeleteField = (field: TLayoutField): void =>
    setDeleteDialog({
      title: translate('delete_custom_field', { fieldName: field.label }),
      message: translate('delete_custom_field_message'),
      confirmText: translate('delete_custom_field_confirm'),
      confirm: () => deleteCustomField(field.id),
    });

  const handleDeleteConfirmed = (): void => {
    deleteDialog?.confirm();
    setDeleteDialog(null);
  };

  const handleDeleteDialogOpenChange = (details: { open: boolean }): void => {
    if (!details.open) {
      setDeleteDialog(null);
    }
  };

  const handleEditCustomField = (fieldId: string) => {
    editCustomField(fieldId);
  };

  return (
    <Flex direction="column" gap={4} alignSelf="stretch" h="full" minH="0" overflow="hidden">
      <Flex gap={4} direction="row" w="full" align="right" justify="end">
        <VButton
          prefixIcon={AddIcon}
          color="secondary"
          onClick={handleCreateGroup}
          activated={false}
          href={null}
        >
          {translate('create_group')}
        </VButton>

        <VButton
          prefixIcon={AddIcon}
          onClick={() => handleAddCustomField()}
          activated={false}
          href={null}
        >
          {translate('add_custom_field')}
        </VButton>
      </Flex>

      <NewCustomFieldGroupModal
        open={isNewGroupModalOpen}
        onClose={() => setIsNewGroupModalOpen(false)}
        onCreate={handleGroupCreated}
      />

      {groupToRename ? (
        <RenameCustomFieldGroupModal
          groupName={groupToRename.title}
          onClose={() => setGroupToRename(null)}
          onRename={handleGroupRenamed}
        />
      ) : null}

      {deleteDialog ? (
        <VDialogPW
          open
          onOpenChange={handleDeleteDialogOpenChange}
          onOk={handleDeleteConfirmed}
          onCancel={() => setDeleteDialog(null)}
          loading={false}
          saveButtonColor="destructive"
          title={deleteDialog.title}
          body={<Text>{deleteDialog.message}</Text>}
          saveBtnText={deleteDialog.confirmText}
        />
      ) : null}

      <hr />

      {layout.length > 0 ? (
        <Box flex="1" minH="0" overflowY="auto">
          <VDraggable<TLayoutEntry, TLayoutItem>
            containers={layout}
            getId={(node) => (isGroup(node) ? node.id ?? node.title : node.id)}
            isContainer={isGroup}
            getItems={(entry) => (isGroup(entry) ? entry.items : [])}
            setItems={(entry, items) => (isGroup(entry) ? { ...entry, items } : entry)}
            onChange={setLayout}
            onDrop={handleReorder}
            renderItem={(item) => (isGroup(item) ? null : <span>{item.label}</span>)}
            renderEmptyContainer={(container) => (
              <Flex
                data-testid="empty-group-drop-zone"
                flexDirection="column"
                gap={2}
                m={4}
                padding={4}
                alignItems="center"
                borderWidth="2px"
                borderStyle="dashed"
                borderRadius={4}
              >
                <b>Drag custom fields here</b>
                <p>Click the button below and create new custom field directly inside group</p>
                <Box py="2">
                  <VButton
                    prefixIcon={AddIcon}
                    color="secondary"
                    onClick={() =>
                      handleAddCustomField(
                        isGroup(container) ? toGroupOption(container) : undefined,
                      )
                    }
                  >
                    Add Custom Field
                  </VButton>
                </Box>
              </Flex>
            )}
          >
            {(entry, content, dragHandle, isDragging, isPreview) =>
              isGroup(entry) ? (
                <CustomFieldGroup
                  group={entry}
                  content={content}
                  dragHandle={dragHandle}
                  onAddField={() => handleAddCustomField(toGroupOption(entry))}
                  isPreview={isPreview}
                  onUngroup={() => handleUngroup(entry, layout)}
                  onRename={() => setGroupToRename(entry)}
                  onDeleteGroup={() => askToDeleteGroup(entry)}
                  onSetGroupVisibility={(display) => setGroupVisibility(entry, display)}
                  onSetGroupLocked={(locked) => setGroupLocked(entry, locked)}
                />
              ) : (
                <SettingsCustomFieldRow
                  field={entry}
                  content={content}
                  dragHandle={dragHandle}
                  isGrouped={groupedFieldIds.has(entry.id)}
                  onDeleteCustomField={() => askToDeleteField(entry)}
                  onSetVisibility={(display) => setFieldVisibility(entry, display)}
                  onSetLocked={(locked) => setFieldLocked(entry, locked)}
                  onEditCustomField={(fieldId) => handleEditCustomField(fieldId)}
                />
              )
            }
          </VDraggable>
        </Box>
      ) : (
        <VEmptyState title="No Custom Fields yet" />
      )}
    </Flex>
  );
};

export default CustomFieldGroupsSettings;
